import { useEffect, useMemo, useState } from "react"
import { useParams, useHistory } from "react-router-dom"
import { ClockCircleOutlined, InfoCircleFilled } from '@ant-design/icons'
import { BrowserTabs, TrustScore } from "src/assets/icons"
import Modal from "src/components/Common/Modal"
import Tab from "src/components/Common/Tab"
import SnapshotsContainer from "./ProctoredMedia/SnapshotsContainer"
import VideoProctoringContainer from "./ProctoredMedia/VideoProctoringContainer"
import AudioProctoringContainer from "./ProctoredMedia/AudioProctoringContainer"
import { TrustScorePill } from "src/components/Common/TrustScore"
import WithToolTip from "src/components/Common/WithToolTip"
import { ProfessionalReadinessPill } from "src/components/Common/ProfessionalReadinessPill"
import PdfReportApi from "src/services/apis/pdfReport"
import { capitalizeTitle, getDefaultFormattedDate, roundOff } from "src/utils/helperFunctions"
import { PROCTORING_TYPES } from "src/constants/globalConstants"
import { AI_PROCTORING } from "../constant"
import { getTraitBand } from "../oprations"

const hasSnapshotCriticalReason = (snap) => {
    if (typeof snap?.criticalReason === "string") {
        return snap.criticalReason.trim().length > 0
    }
    return !!snap?.criticalReason
}

const getSnapshotTimeInSeconds = (snap, fallbackSeconds) => {
    const timestamp = Number(snap?.timestamp)
    if (!Number.isNaN(timestamp)) return timestamp

    const createdAt = new Date(snap?.createdAt).getTime()
    if (!Number.isNaN(createdAt)) return Math.floor(createdAt / 1000)

    return fallbackSeconds
}

const filterSkippedAiSnapshots = (snapshots, intervalInSeconds = 30) => {
    let intervalAnchor = null

    return snapshots.filter((snap, index) => {
        const snapshotTime = getSnapshotTimeInSeconds(snap, index * intervalInSeconds)
        if (intervalAnchor === null) intervalAnchor = snapshotTime

        if (hasSnapshotCriticalReason(snap)) {
            return true
        }

        if (index === 0 && !snap?.aiProctoringData) return false

        return (snapshotTime - intervalAnchor) % intervalInSeconds === 0
    })
}

export const ProctoringModal = ({
    isProctoringModalVisible,
    setIsProctoringModalVisible,
    linkConfig,
    positionConfig,
    userVideoSessionsList,
    setUserVideoSessionsList,
    allSnapshots,
    setAllSnapshots,
    criticalSnapshots,
    setCriticalSnapshots,
    tabSwitched,
    trustScore,
    fetchSessionSnapshots,
    cutOffCleared,
    referenceImageLink,
    audioProctoringReport,
}) => {
    const [isLoading, setIsLoading] = useState(false)
    const [currentProctoringType, setCurrentProctoringType] = useState("")
    const [screenVideoSessionsList, setScreenVideoSessionsList] = useState([])
    const [audioSessionsList, setAudioSessionsList] = useState([])
    const { linkId } = useParams()
    const history = useHistory()

    const isCandidateReport = useMemo(() => history?.location?.pathname?.includes("candidate-report"), [history])

    const [isImgProctored, isVideoProctored, isScreenProctored, isAudioProctored] = useMemo(() => [
        (linkConfig?.proctoring),
        (linkConfig?.saveProctoredVideo),
        (positionConfig?.screenProctoring),
        (positionConfig?.audioProctoring)
    ], [positionConfig, linkConfig])

    const proctoringTypeOptions = useMemo(() => {
        let options = []
        if (isImgProctored || isVideoProctored) options.push({
            name: "User",
            key: "user",
        })
        if (isScreenProctored) options.push({
            name: "Screen",
            key: "screen",
        })
        if (isAudioProctored) options.push({
            name: "Audio",
            key: "audio",
        })
        return options
    }, [isImgProctored, isVideoProctored, isScreenProctored, isAudioProctored])

    const [trustScoreColor, trustScoreBg, trustScoreVal] = useMemo(() => {
        let textColor = "#B6B6B6", bgColor = "#fff", trustScoreNum = Number(trustScore?.trustScore?.toString()?.split("%")?.[0])
        if (trustScore?.remark) {
            const bandStyle = getTraitBand({
                median: 50,
                lb: 25,
                ub: 75,
                score: roundOff(trustScoreNum),
            })
            textColor = bandStyle.textColor
            bgColor = bandStyle.color
        }
        return [textColor, bgColor, trustScoreNum]
    }, [trustScore])

    const trustScoreReceivedAt = trustScore?.receivedAt
    const hideTrustScoreDescription = positionConfig?.skipAiProcOnCutoffFail && cutOffCleared === false

    useEffect(() => {
        if (isImgProctored || (isVideoProctored || userVideoSessionsList.length)) setCurrentProctoringType("user")
        else if (isScreenProctored) setCurrentProctoringType("screen")
        else if (isAudioProctored) setCurrentProctoringType("audio")
    }, [isImgProctored, isVideoProctored, isScreenProctored, isAudioProctored, userVideoSessionsList])

    useEffect(() => {
        if (isImgProctored) {
            fetchImgProctoring()
        }
    }, [isImgProctored, positionConfig, linkConfig])

    useEffect(() => {
        if (isVideoProctored || linkConfig === null) {
            fetchUserVideoSessions()
        }
    }, [isVideoProctored, positionConfig, linkConfig])

    useEffect(() => {
        if (isScreenProctored) {
            fetchScreenVideoSessions()
        }
    }, [isScreenProctored, positionConfig, linkConfig])

    useEffect(() => {
        if (isAudioProctored) {
            fetchAudioSessions()
        }
    }, [isAudioProctored, positionConfig])

    const fetchUserVideoSessions = async () => {
        try {
            const response = await PdfReportApi.getVideoSessions(linkId, PROCTORING_TYPES.VIDEO)
            if (response.success) {
                setUserVideoSessionsList(response.data)
            }
        } catch (err) {
            console.error(err)
        }
    }

    const fetchScreenVideoSessions = async () => {
        try {
            const response = await PdfReportApi.getVideoSessions(linkId, PROCTORING_TYPES.SCREEN)
            if (response.success) {
                setScreenVideoSessionsList(response.data)
            }
        } catch (err) {
            console.error(err)
        }
    }

    const fetchAudioSessions = async () => {
        try {
            const response = await PdfReportApi.getVideoSessions(linkId, PROCTORING_TYPES.AUDIO)
            if (response.success) {
                setAudioSessionsList(response.data)
            }
        } catch (err) {
            console.error(err)
        }
    }

    const fetchImgProctoring = async () => {
        try {
            setIsLoading(true)
            const response = await PdfReportApi.fetchSnapshots(linkId)
            if (response.success) {
                let sortedSnapshots = response.data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                let updatedSnapshotsData = []
                if (positionConfig.aiProctoring) {
                    const showAllSnapshots = positionConfig.skipAiProcOnCutoffFail && cutOffCleared === false
                    const aiProctoringInterval = Number(positionConfig?.aiProctoringInterval) || 30
                    updatedSnapshotsData = showAllSnapshots
                        ? filterSkippedAiSnapshots(sortedSnapshots, aiProctoringInterval)
                        : sortedSnapshots.filter(snap => !!snap?.aiProctoringData)

                    updatedSnapshotsData = updatedSnapshotsData.map(snap => {
                        const isCritical = [AI_PROCTORING.TYPES.CHEATING, AI_PROCTORING.TYPES.MAYBECHEATING].includes(snap?.aiProctoringData?.status)
                        const hasSkippedAiCriticalReason = !snap?.aiProctoringData && hasSnapshotCriticalReason(snap)
                        const personalityReadiness = positionConfig.trustScorePersonalityAnalysis ? snap?.aiProctoringData?.professionalReadiness : null

                        const criticalContent = isCritical ? <>
                            <p className="text-white text-14 weight-700 mb-4">{capitalizeTitle(snap?.aiProctoringData?.type)}</p>
                            <p className="text-white text-12 weight-400">{snap?.aiProctoringData?.remark}</p>
                        </> : hasSkippedAiCriticalReason ? (
                            <p className="text-white text-12 weight-400">{snap.criticalReason}</p>
                        ) : null

                        const readinessContent = personalityReadiness ? <div
                            className={isCritical ? "mt-8 pt-8" : ""}
                        >
                            <p className="text-white text-13 weight-700 mb-4">Professional Readiness</p>
                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.3)' }} className="mb-8" />
                            <p className="text-white text-13 weight-700 mb-2">Overall Score: {roundOff(personalityReadiness.overallScore)}</p>
                            {personalityReadiness.overallSummary && <p className="text-white text-12 weight-400 mb-6">{personalityReadiness.overallSummary}</p>}
                            <p className="text-white text-12 mb-1"><span className="weight-700">Facial Expressions: {roundOff(personalityReadiness.facialExpressions?.score)}</span>{personalityReadiness.facialExpressions?.remark ? ` (${personalityReadiness.facialExpressions.remark})` : ""}</p>
                            <p className="text-white text-12 mb-1"><span className="weight-700">Eye Contact: {roundOff(personalityReadiness.eyeContact?.score)}</span>{personalityReadiness.eyeContact?.remark ? ` (${personalityReadiness.eyeContact.remark})` : ""}</p>
                            <p className="text-white text-12 mb-1"><span className="weight-700">Grooming: {roundOff(personalityReadiness.grooming?.score)}</span>{personalityReadiness.grooming?.remark ? ` (${personalityReadiness.grooming.remark})` : ""}</p>
                            {personalityReadiness.professionalConduct && <p className="text-white text-12"><span className="weight-700">Professional Conduct: {roundOff(personalityReadiness.professionalConduct?.score)}</span>{personalityReadiness.professionalConduct?.remark ? ` (${personalityReadiness.professionalConduct.remark})` : ""}</p>}
                        </div> : null

                        return {
                            ...snap,
                            critical: isCritical || hasSkippedAiCriticalReason,
                            criticalType: hasSkippedAiCriticalReason ? AI_PROCTORING.TYPES.CHEATING : snap?.aiProctoringData?.status,
                            hasReadinessTooltip: !!personalityReadiness,
                            criticalReason: (criticalContent || readinessContent) ? <>
                                {criticalContent}
                                {readinessContent}
                            </> : null,
                        }
                    })
                } else {
                    updatedSnapshotsData = sortedSnapshots.map(snap => ({ ...snap, criticalType: AI_PROCTORING.TYPES.CHEATING }))
                }
                setAllSnapshots(updatedSnapshotsData)
                setCriticalSnapshots(updatedSnapshotsData.filter(snap => snap.critical))
            }
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Modal
            modalVisible={isProctoringModalVisible}
            cancelModal={() => {
                setIsProctoringModalVisible(false)
            }}
            top={10}
            width={"fit-content"}
            wrapClassName="proctoring-wrap"
            className={`proctoring-modal${currentProctoringType === "audio" ? " proctoring-modal--audio" : ""}`}
        >
            <div className="flex items-center justify-start mb-6">
                <p className="text-24 weight-700 mr-10">Proctoring Details</p>
                {
                    trustScore?.remark ? <TrustScorePill
                        icon={<TrustScore color={trustScoreColor} />}
                        text={`${roundOff(trustScoreVal)}%`}
                        bg={trustScoreBg}
                        color={trustScoreColor}
                        tooltipText={hideTrustScoreDescription ? "" : trustScore.remark}
                    /> : <></>
                }
                {
                    positionConfig?.trustScorePersonalityAnalysis && trustScore?.professionalReadiness
                        ? <ProfessionalReadinessPill
                            professionalReadiness={trustScore.professionalReadiness}
                            className="ml-4"
                        />
                        : <></>
                }
                {
                    trustScoreReceivedAt
                        ? <WithToolTip
                            title="Trust Score Received At"
                            showToolTip={true}
                        >
                            <span className="proctoring-result-updated-pill flex items-center ml-8 text-12 weight-600 pl-2 pr-12 py-2 b-20 select-none">
                                <span className="proctoring-result-updated-pill--icon px-8 py-4 mr-8 b-40">
                                    <ClockCircleOutlined />
                                </span>
                                {getDefaultFormattedDate(trustScoreReceivedAt, "Do MMM YYYY, hh:mm A")}
                            </span>
                        </WithToolTip>
                        : <></>
                }
            </div>
            {
                !isCandidateReport
                    ? <span className="flex items-center mb-6 opacity-6 weight-600 color-black text-14">
                        <span className="mr-4"><BrowserTabs /></span>
                        Switched tabs &#40;{tabSwitched ?? "0"}&#41;
                    </span>
                    : <></>
            }
            {
                linkConfig?.proctoring === false
                    && linkConfig?.saveProctoredVideo === false
                    && positionConfig?.saveProctoredVideo
                    ? <div className="flex items-center mb-6">
                        <span className="flex items-center config-msg-icon">
                            <InfoCircleFilled style={{ color: '#d24444' }} className="text-18" />
                        </span>
                        <p className={`text-12 color-black ml-4 letter-spacing-3 config-msg `}>
                            <strong>Video Proctoring</strong> disabled by admin for this user
                        </p>
                    </div>
                    : <></>
            }
            {/* <p className={`text-12 color-black letter-spacing-3 config-msg`}>
                {
                    linkConfig?.proctoring && positionConfig?.saveProctoredVideo
                        ? <><strong>Video Proctoring</strong> was changed to <strong>Image Proctoring</strong> by admin</>
                        : linkConfig?.proctoring === false && linkConfig?.saveProctoredVideo === false
                            && positionConfig?.proctoring
                            ? <><strong>Image Proctoring</strong> disabled by admin for this user</>
                            : <></>
                }
            </p> */}
            <div className="mt-20">
                {
                    proctoringTypeOptions.length > 1
                        ? <Tab
                            currentKey={currentProctoringType}
                            data={proctoringTypeOptions}
                            onClick={(type) => {
                                setCurrentProctoringType(type)
                            }}
                        /> : <></>
                }
            </div>
            {
                currentProctoringType === "user"
                    ? isImgProctored
                        ? <SnapshotsContainer
                            isImgProctoringModal={true}
                            allSnapshots={allSnapshots}
                            criticalSnapshots={criticalSnapshots}
                            isAiProctored={(positionConfig?.screenProctoring || positionConfig?.aiProctoring)}
                            referenceImageLink={referenceImageLink}
                        />
                        : (isVideoProctored || userVideoSessionsList?.length > 0)
                            ? <VideoProctoringContainer
                                key={"user"}
                                videoSessionsList={userVideoSessionsList}
                                fetchSessionSnapshots={fetchSessionSnapshots}
                                isAiProctored={(positionConfig?.screenProctoring || positionConfig?.aiProctoring)}
                                referenceImageLink={referenceImageLink}
                                audioProctoring={positionConfig?.audioProctoring}
                            />
                            : <></>
                    : currentProctoringType === "screen"
                        ? <VideoProctoringContainer
                            key={"screen"}
                            videoSessionsList={screenVideoSessionsList}
                            isParentScreenProctoring={true}
                            fetchSessionSnapshots={fetchSessionSnapshots}
                            isAiProctored={(positionConfig?.screenProctoring || positionConfig?.aiProctoring)}
                            referenceImageLink={referenceImageLink}
                            audioProctoring={positionConfig?.audioProctoring}
                        />
                        : currentProctoringType === "audio"
                            ? <AudioProctoringContainer
                                key={"audio"}
                                audioSessionsList={audioSessionsList}
                                audioProctoringReport={audioProctoringReport}
                            />
                            : <></>
            }
        </Modal>
    )
}
